import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { connect } from 'trim-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { defaultVariables } from '../../../../config';
import Title from '../../../components/elements/Title';
import LoadingButton from '../../../components/elements/LoadingButton';
import {
    isNotificationManagerUnlocked,
    notificationManagerHeaders,
    setNotificationManagerUnlocked,
} from '../../../../config/notificationManagerAuth';

const CHANNEL_I18N = {
    database: 'notificationManagerChannelDatabase',
    push: 'notificationManagerChannelPush',
    email: 'notificationManagerChannelEmail',
    order_alert: 'notificationManagerChannelOrderAlert',
};

const GROUP_I18N = {
    order: 'notificationManagerGroupOrder',
    courier: 'notificationManagerGroupCourier',
    message: 'notificationManagerGroupMessage',
    ticket: 'notificationManagerGroupTicket',
    comment: 'notificationManagerGroupComment',
    charity: 'notificationManagerGroupCharity',
};

const RECIPIENT_I18N = {
    user: 'notificationManagerRecipientUser',
    vendor: 'notificationManagerRecipientVendor',
    courier: 'notificationManagerRecipientCourier',
    support: 'notificationManagerRecipientSupport',
    charity: 'notificationManagerRecipientCharity',
};

function formatPushDeliverySummary(delivery) {
    if (!delivery || typeof delivery !== 'object') {
        return null;
    }
    if (delivery.skipped) {
        return `skipped:${delivery.reason || 'unknown'}`;
    }
    const parts = ['web', 'desktop', 'app'].map((key) => {
        const value = delivery[key];
        if (!value) {
            return null;
        }
        const errKey = `${key}_error`;
        return delivery[errKey] ? `${key}:${value}(${delivery[errKey]})` : `${key}:${value}`;
    }).filter(Boolean);
    return parts.length > 0 ? parts.join(' | ') : null;
}

function extractPushDeliveryFromSent(sent) {
    if (!Array.isArray(sent)) {
        return null;
    }
    const pushEntry = sent.find((item) => item?.channel === 'push');
    if (!pushEntry) {
        return null;
    }
    return pushEntry.delivery || pushEntry;
}

function NotificationManagerPage({ user }) {
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [unlocked, setUnlocked] = useState(() => isNotificationManagerUnlocked());
    const [passInput, setPassInput] = useState('');
    const [passLoading, setPassLoading] = useState(false);

    const [scenarios, setScenarios] = useState([]);
    const [loadingScenarios, setLoadingScenarios] = useState(false);

    const [orderQuery, setOrderQuery] = useState('');
    const [orderOptions, setOrderOptions] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderLoading, setOrderLoading] = useState(false);
    const [recipients, setRecipients] = useState(null);

    const [userQuery, setUserQuery] = useState('');
    const [userOptions, setUserOptions] = useState([]);
    const [userLoading, setUserLoading] = useState(false);

    const [sendingId, setSendingId] = useState(null);
    const [channelFilter, setChannelFilter] = useState('all');

    const [custom, setCustom] = useState({
        user: null,
        sendToAll: false,
        title: '',
        body: '',
        link: '/',
        image: '',
        type: 'info',
        channels: { database: true, push: true, email: false },
    });
    const [customSending, setCustomSending] = useState(false);
    const [confirmAllOpen, setConfirmAllOpen] = useState(false);

    const apiBase = `${defaultVariables.url}/notification-manager`;

    const channelLabel = useCallback(
        (ch) => t(CHANNEL_I18N[ch] || ch),
        [t],
    );

    const groupLabel = useCallback(
        (group) => t(GROUP_I18N[group] || group),
        [t],
    );

    const scenarioLabel = useCallback(
        (id) => t(`nmScenario_${id}`, id),
        [t],
    );

    const scenarioNote = useCallback(
        (scenario) => {
            if (scenario.note_key === 'charity_required') {
                return t('notificationManagerNoteCharityRequired');
            }
            return null;
        },
        [t],
    );

    useEffect(() => {
        if (user?.role?.name !== 'admin') {
            navigate('/panel/manage/setting/view-all');
        }
    }, [user, navigate]);

    const loadScenarios = useCallback(async () => {
        setLoadingScenarios(true);
        try {
            const res = await axios.get(`${apiBase}/scenarios`, {
                headers: notificationManagerHeaders(),
            });
            setScenarios(res.data?.groups ?? []);
        } catch (e) {
            if (e.response?.status === 403) {
                setUnlocked(false);
                setNotificationManagerUnlocked(false);
            }
            enqueueSnackbar(t('notificationManagerScenariosLoadFailed'), { variant: 'error' });
        } finally {
            setLoadingScenarios(false);
        }
    }, [apiBase, enqueueSnackbar, t]);

    useEffect(() => {
        if (unlocked) {
            loadScenarios();
        }
    }, [unlocked, loadScenarios]);

    const searchOrders = useCallback(async (q) => {
        setOrderLoading(true);
        try {
            const res = await axios.get(`${apiBase}/orders/search`, {
                headers: notificationManagerHeaders(),
                params: { q: q || undefined, limit: 25 },
            });
            setOrderOptions(res.data ?? []);
        } catch (e) {
            console.error(e);
        } finally {
            setOrderLoading(false);
        }
    }, [apiBase]);

    useEffect(() => {
        if (!unlocked) return undefined;
        const timerId = setTimeout(() => searchOrders(orderQuery), 350);
        return () => clearTimeout(timerId);
    }, [orderQuery, unlocked, searchOrders]);

    useEffect(() => {
        if (!selectedOrder?.id || !unlocked) {
            setRecipients(null);
            return;
        }
        axios
            .get(`${apiBase}/orders/${selectedOrder.id}/recipients`, {
                headers: notificationManagerHeaders(),
            })
            .then((r) => setRecipients(r.data))
            .catch(() => setRecipients(null));
    }, [selectedOrder, unlocked, apiBase]);

    const searchUsers = useCallback(async (q) => {
        setUserLoading(true);
        try {
            const res = await axios.get(`${apiBase}/users/search`, {
                headers: notificationManagerHeaders(),
                params: { q: q || undefined },
            });
            setUserOptions(res.data ?? []);
        } catch (e) {
            console.error(e);
        } finally {
            setUserLoading(false);
        }
    }, [apiBase]);

    useEffect(() => {
        if (!unlocked) return undefined;
        const timerId = setTimeout(() => searchUsers(userQuery), 350);
        return () => clearTimeout(timerId);
    }, [userQuery, unlocked, searchUsers]);

    const handleUnlock = async () => {
        setPassLoading(true);
        try {
            await axios.post(
                `${apiBase}/verify`,
                { manager_password: passInput },
                { headers: notificationManagerHeaders(passInput) },
            );
            setNotificationManagerUnlocked(true);
            setUnlocked(true);
            enqueueSnackbar(t('notificationManagerUnlocked'), { variant: 'success' });
        } catch {
            enqueueSnackbar(t('notificationManagerWrongPassword'), { variant: 'error' });
        } finally {
            setPassLoading(false);
        }
    };

    const sendScenario = async (scenario) => {
        if (scenario.requires_order && !selectedOrder?.id) {
            enqueueSnackbar(t('notificationManagerSelectOrder'), { variant: 'warning' });
            return;
        }
        setSendingId(scenario.id);
        try {
            const res = await axios.post(
                `${apiBase}/send-scenario`,
                {
                    scenario_id: scenario.id,
                    order_id: selectedOrder?.id ?? null,
                    target_user_id: custom.user?.id ?? null,
                    channels: scenario.default_channels,
                },
                { headers: notificationManagerHeaders() },
            );
            if (res.data?.ok) {
                const deliverySummary = formatPushDeliverySummary(
                    extractPushDeliveryFromSent(res.data?.sent),
                );
                enqueueSnackbar(
                    deliverySummary
                        ? `${t('notificationManagerSent')}: ${scenarioLabel(scenario.id)} — ${deliverySummary}`
                        : `${t('notificationManagerSent')}: ${scenarioLabel(scenario.id)}`,
                    { variant: 'success', autoHideDuration: deliverySummary ? 12000 : 4000 },
                );
            } else {
                enqueueSnackbar(res.data?.error || t('notificationManagerSendFailed'), { variant: 'error' });
            }
        } catch (e) {
            enqueueSnackbar(e.response?.data?.error || t('notificationManagerSendFailed'), { variant: 'error' });
        } finally {
            setSendingId(null);
        }
    };

    const executeSendCustom = async () => {
        const channels = Object.entries(custom.channels)
            .filter(([, v]) => v)
            .map(([k]) => k);
        setCustomSending(true);
        try {
            const res = await axios.post(
                `${apiBase}/send-custom`,
                {
                    send_to_all: custom.sendToAll,
                    user_id: custom.sendToAll ? null : custom.user?.id,
                    title: custom.title,
                    body: custom.body,
                    link: custom.link || '/',
                    image: custom.image || null,
                    type: custom.type,
                    channels,
                },
                { headers: notificationManagerHeaders() },
            );
            if (res.data?.ok) {
                if (res.data.broadcast && res.data.users_count) {
                    enqueueSnackbar(
                        t('notificationManagerCustomSentAll', { count: res.data.users_count }),
                        { variant: 'success' },
                    );
                } else {
                    const deliverySummary = formatPushDeliverySummary(
                        extractPushDeliveryFromSent(res.data?.sent),
                    );
                    enqueueSnackbar(
                        deliverySummary
                            ? `${t('notificationManagerCustomSent')} — ${deliverySummary}`
                            : t('notificationManagerCustomSent'),
                        { variant: 'success', autoHideDuration: deliverySummary ? 12000 : 4000 },
                    );
                }
            } else {
                enqueueSnackbar(res.data?.error || t('notificationManagerFailed'), { variant: 'error' });
            }
        } catch (e) {
            enqueueSnackbar(e.response?.data?.error || t('notificationManagerFailed'), { variant: 'error' });
        } finally {
            setCustomSending(false);
            setConfirmAllOpen(false);
        }
    };

    const sendCustom = () => {
        if (!custom.sendToAll && !custom.user?.id) {
            enqueueSnackbar(t('notificationManagerCustomRequired'), { variant: 'warning' });
            return;
        }
        if (!custom.title.trim() || !custom.body.trim()) {
            enqueueSnackbar(t('notificationManagerCustomRequired'), { variant: 'warning' });
            return;
        }
        const channels = Object.entries(custom.channels)
            .filter(([, v]) => v)
            .map(([k]) => k);
        if (!channels.length) {
            enqueueSnackbar(t('notificationManagerSelectChannel'), { variant: 'warning' });
            return;
        }
        if (custom.sendToAll) {
            setConfirmAllOpen(true);
            return;
        }
        executeSendCustom();
    };

    const filteredScenarios = useMemo(() => {
        if (channelFilter === 'all') {
            return scenarios;
        }
        return scenarios
            .map((group) => ({
                ...group,
                scenarios: (group.scenarios || []).filter((scenario) =>
                    (scenario.default_channels || []).includes(channelFilter),
                ),
            }))
            .filter((group) => (group.scenarios || []).length > 0);
    }, [scenarios, channelFilter]);

    const pushScenarioCount = useMemo(() => {
        return scenarios.reduce(
            (count, group) =>
                count +
                (group.scenarios || []).filter((s) =>
                    (s.default_channels || []).includes('push'),
                ).length,
            0,
        );
    }, [scenarios]);

    const recipientChips = useMemo(() => {
        if (!recipients) return null;
        const items = [
            ['user', recipients.user],
            ['vendor', recipients.vendor],
            ['courier', recipients.courier],
            ['support', recipients.support],
            ['charity', recipients.charity_owner],
        ].filter(([, v]) => v);
        return items.map(([role, u]) => {
            const webOk = u.push_web?.push_ready;
            const appOk = u.push_app?.push_ready;
            const desktopOk = u.push_desktop?.push_ready;
            const pushHint = `W:${webOk ? '✓' : '✗'} A:${appOk ? '✓' : '✗'} D:${desktopOk ? '✓' : '✗'}`;
            const roleLabel = t(RECIPIENT_I18N[role] || role);
            return (
                <Chip
                    key={role}
                    size="small"
                    color={webOk || appOk || desktopOk ? 'success' : 'warning'}
                    variant={webOk || appOk || desktopOk ? 'filled' : 'outlined'}
                    label={`${roleLabel}: #${u.id} ${u.name} [${pushHint}]`}
                    title={[
                        webOk ? t('pushTokenWebReady') : t('pushTokenWebMissing'),
                        appOk ? t('pushTokenAppReady') : t('pushTokenAppMissing'),
                        desktopOk ? t('pushTokenDesktopReady') : t('pushTokenDesktopMissing'),
                    ].join(' | ')}
                    onClick={() => setCustom((c) => ({ ...c, sendToAll: false, user: { id: u.id, label: `#${u.id} ${u.name}` } }))}
                />
            );
        });
    }, [recipients, t]);

    if (!unlocked) {
        return (
            <Box sx={{ maxWidth: 420, mx: 'auto', mt: 8, p: 3 }}>
                <Paper sx={{ p: 3 }}>
                    <Title color="text" align="left">{t('notificationManagerTitle')}</Title>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                        {t('notificationManagerUnlockHint')}
                    </Typography>
                    <TextField
                        fullWidth
                        type="password"
                        label={t('notificationManagerPassword')}
                        value={passInput}
                        onChange={(e) => setPassInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
                    />
                    <LoadingButton
                        fullWidth
                        sx={{ mt: 2 }}
                        loading={passLoading}
                        startIcon={<LockOpenRoundedIcon />}
                        onClick={handleUnlock}
                        label={t('notificationManagerUnlock')}
                    />
                </Paper>
            </Box>
        );
    }

    return (
        <Box className="manage-holder" sx={{ pb: 6 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Title color="text" align="left">{t('notificationManagerTitle')}</Title>
                <Button
                    size="small"
                    color="inherit"
                    onClick={() => {
                        setNotificationManagerUnlocked(false);
                        setUnlocked(false);
                    }}
                >
                    {t('notificationManagerLock')}
                </Button>
            </Stack>

            <Alert severity="info" sx={{ mb: 2 }}>
                {t('notificationManagerInfo')}
            </Alert>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {t('notificationManagerOrderSearch')}
                </Typography>
                <Autocomplete
                    options={orderOptions}
                    loading={orderLoading}
                    getOptionLabel={(o) => o.label || o.number || ''}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    value={selectedOrder}
                    onChange={(_, v) => setSelectedOrder(v)}
                    onInputChange={(_, v) => setOrderQuery(v)}
                    renderInput={(params) => (
                        <TextField {...params} placeholder={t('notificationManagerOrderPlaceholder')} />
                    )}
                />
                {recipientChips?.length > 0 && (
                    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{ alignSelf: 'center' }}>
                            {t('notificationManagerRecipients')}
                        </Typography>
                        {recipientChips}
                    </Stack>
                )}
            </Paper>

            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                {[
                    ['all', t('notificationManagerFilterAll')],
                    ['push', `${t('notificationManagerChannelPush')} (${pushScenarioCount})`],
                    ['database', t('notificationManagerChannelDatabase')],
                    ['email', t('notificationManagerChannelEmail')],
                    ['order_alert', t('notificationManagerChannelOrderAlert')],
                ].map(([value, label]) => (
                    <Chip
                        key={value}
                        label={label}
                        color={channelFilter === value ? 'primary' : 'default'}
                        variant={channelFilter === value ? 'filled' : 'outlined'}
                        onClick={() => setChannelFilter(value)}
                    />
                ))}
            </Stack>

            {loadingScenarios ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                filteredScenarios.map((group) => (
                    <Accordion key={group.group} defaultExpanded={group.group === 'order'}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography fontWeight={700}>
                                {groupLabel(group.group)}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={1}>
                                {(group.scenarios || []).map((scenario) => {
                                    const note = scenarioNote(scenario);
                                    return (
                                        <Grid size={{ xs: 12, md: 6 }} key={scenario.id}>
                                            <Paper variant="outlined" sx={{ p: 1.5 }}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {scenarioLabel(scenario.id)}
                                                </Typography>
                                                <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ my: 1 }}>
                                                    {(scenario.default_channels || []).map((ch) => (
                                                        <Chip key={ch} size="small" label={channelLabel(ch)} />
                                                    ))}
                                                    {scenario.urgent_push && (
                                                        <Chip
                                                            size="small"
                                                            color="warning"
                                                            label={t('notificationManagerUrgentPush')}
                                                        />
                                                    )}
                                                </Stack>
                                                {note && (
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {note}
                                                    </Typography>
                                                )}
                                                <LoadingButton
                                                    size="small"
                                                    sx={{ mt: 1 }}
                                                    loading={sendingId === scenario.id}
                                                    startIcon={<SendRoundedIcon />}
                                                    onClick={() => sendScenario(scenario)}
                                                    label={t('notificationManagerSendTest')}
                                                />
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                ))
            )}

            <Divider sx={{ my: 3 }} />

            <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {t('notificationManagerCustomTitle')}
                </Typography>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={custom.sendToAll}
                                    onChange={(e) =>
                                        setCustom((c) => ({
                                            ...c,
                                            sendToAll: e.target.checked,
                                            user: e.target.checked ? null : c.user,
                                        }))
                                    }
                                />
                            }
                            label={t('notificationManagerSendToAll')}
                        />
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 4, mt: -0.5 }}>
                            {t('notificationManagerSendToAllHint')}
                        </Typography>
                    </Grid>
                    <Grid size={12}>
                        <Autocomplete
                            options={userOptions}
                            loading={userLoading}
                            disabled={custom.sendToAll}
                            getOptionLabel={(o) => o.label || ''}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            value={custom.sendToAll ? null : custom.user}
                            onChange={(_, v) => setCustom((c) => ({ ...c, sendToAll: false, user: v }))}
                            onInputChange={(_, v) => setUserQuery(v)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('notificationManagerTargetUser')}
                                    placeholder={
                                        custom.sendToAll
                                            ? t('notificationManagerTargetAll')
                                            : t('notificationManagerTargetUserPlaceholder')
                                    }
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label={t('notificationManagerFieldTitle')}
                            value={custom.title}
                            onChange={(e) => setCustom((c) => ({ ...c, title: e.target.value }))}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                            fullWidth
                            label={t('notificationManagerFieldLink')}
                            value={custom.link}
                            onChange={(e) => setCustom((c) => ({ ...c, link: e.target.value }))}
                        />
                    </Grid>
                    <Grid size={12}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            label={t('notificationManagerFieldBody')}
                            value={custom.body}
                            onChange={(e) => setCustom((c) => ({ ...c, body: e.target.value }))}
                        />
                    </Grid>
                    <Grid size={12}>
                        <TextField
                            fullWidth
                            label={t('notificationManagerFieldImage')}
                            value={custom.image}
                            onChange={(e) => setCustom((c) => ({ ...c, image: e.target.value }))}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                            {['database', 'push', 'email'].map((ch) => (
                                <FormControlLabel
                                    key={ch}
                                    control={
                                        <Checkbox
                                            checked={!!custom.channels[ch]}
                                            onChange={(e) =>
                                                setCustom((c) => ({
                                                    ...c,
                                                    channels: { ...c.channels, [ch]: e.target.checked },
                                                }))
                                            }
                                        />
                                    }
                                    label={channelLabel(ch)}
                                />
                            ))}
                        </Stack>
                    </Grid>
                    <Grid size={12}>
                        <LoadingButton
                            loading={customSending}
                            startIcon={<SendRoundedIcon />}
                            onClick={sendCustom}
                            label={t('notificationManagerSendCustom')}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Dialog open={confirmAllOpen} onClose={() => setConfirmAllOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{t('notificationManagerSendToAll')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">{t('notificationManagerSendToAllConfirm')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmAllOpen(false)} color="inherit">
                        {t('notificationManagerCancel')}
                    </Button>
                    <Button variant="contained" onClick={executeSendCustom} disabled={customSending}>
                        {t('notificationManagerConfirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

const mstp = (state) => ({ user: state.user });

export default connect(mstp)(NotificationManagerPage);
